/**
 * Shared tile-local geometry helpers, used by the mesh builders and the interaction layer.
 *
 * Everything here works in UNROTATED tile-local space: `u,v` in `[0,1]x[0,1]` mirror the canvas
 * convention from `tile-texture.ts` (u=0 west … u=1 east, v=0 north … v=1 south), and `x,z` are the
 * corresponding Three.js group-local coordinates once the top face's own -90°-about-X mesh rotation is
 * accounted for (`x = u - 0.5`, `z = v - 0.5` — see the derivation note in `tile-mesh.ts`).
 */

import { Side, type TileDef } from '@carc/engine';
import { MEEPLE_ANCHORS } from './meeple-anchors.js';

export type TileKind = 'city' | 'road' | 'cloister' | 'field';

/** A single representative colour for a tile type — used by the mini-map, not gameplay. */
export function dominantKind(def: TileDef): TileKind {
  const kinds = new Set(def.features.map((f) => f.kind));
  if (kinds.has('city')) return 'city';
  if (kinds.has('cloister')) return 'cloister';
  if (kinds.has('road')) return 'road';
  return 'field';
}

export type Local = { readonly x: number; readonly z: number };

export const uvToLocal = (u: number, v: number): Local => ({ x: u - 0.5, z: v - 0.5 });

/** The exact boundary point of an unrotated sub-edge (0..11), per the diagram in `types.ts`. */
export function subEdgeUV(subEdge: number): readonly [number, number] {
  const side = Math.floor(subEdge / 3) as Side;
  const offset = subEdge % 3;
  const near = 1 / 6;
  const mid = 0.5;
  const far = 5 / 6;
  switch (side) {
    case Side.North:
      return [[near, mid, far][offset] as number, 0];
    case Side.East:
      return [1, [near, mid, far][offset] as number];
    case Side.South:
      return [[far, mid, near][offset] as number, 1];
    case Side.West:
      return [0, [far, mid, near][offset] as number];
  }
}

export const subEdgeSide = (subEdge: number): Side => Math.floor(subEdge / 3) as Side;

/**
 * Where to anchor a meeple (or a highlight marker) for `featureIndex` on `def`, in tile-local units.
 *
 * Prefers a coordinate solved from the actual artwork (`tools/solve-meeple-anchors.mjs`). The
 * geometric derivation below is the fallback for tiles with no sprite art, and it is only ever
 * approximately right: it averages the midpoints of the sub-edges a feature touches, which is exact
 * for a straight road and wrong for anything that bends. It put the STARTER TILE's road meeple on the
 * grass beside its road — so a player looking at a legitimately occupied road saw an empty one, and
 * reported the rules as broken when the rules were fine ([D-27]).
 */
export function featureAnchor(def: TileDef, featureIndex: number): Local {
  const solved = MEEPLE_ANCHORS[def.id]?.[featureIndex];
  if (solved) return { x: solved.x, z: solved.z };

  const feature = def.features[featureIndex];
  if (!feature || feature.kind === 'cloister' || feature.subEdges.length === 0) {
    return { x: 0, z: 0 };
  }

  let x = 0;
  let z = 0;
  for (const subEdge of feature.subEdges) {
    const local = uvToLocal(...subEdgeUV(subEdge));
    x += local.x;
    z += local.z;
  }
  x /= feature.subEdges.length;
  z /= feature.subEdges.length;

  // Pull the anchor a little toward the centre so it reads as "on" the feature rather than "on the
  // seam" — cities and roads bulge inward from their touched sides, so this keeps meeples off the edge.
  const pull = feature.kind === 'field' ? 0.72 : 0.62;
  return { x: x * pull, z: z * pull };
}
